/**
 * Barcode lookups for scanning.
 *
 * Everything here reads through the normal RLS policies, so a user only ever
 * finds items, products and bookings they could already open elsewhere in the
 * admin panel. Results are additionally scoped to the ACTIVE merchant (a
 * multi-merchant user can read several) and to the user's locations.
 *
 * See docs/BARCODE_SCANNING_SPEC.md, "Scan Resolution".
 */

import { supabase, handleSupabaseError } from '../lib/supabase';
import type { AccessControl } from '../lib/accessControl';
import { normalizeBarcode } from '../lib/barcode';

export interface ScannedUnit {
  id: string;
  nr: number | null;
  item_number: string | null;
  size: string | null;
  active: boolean;
  product_type_id: string;
  /** product_type_id plus alternative / additional product types the unit can serve. */
  product_type_ids: string[];
  product_name: string;
  location_id: string;
}

export interface ScannedProduct {
  id: string;
  product_name: string;
  inventory_type: 'pool' | 'unlimited' | 'individual';
  location_id: string;
}

export type ScanResolution =
  | { kind: 'unit'; code: string; unit: ScannedUnit }
  | { kind: 'product'; code: string; product: ScannedProduct }
  | { kind: 'unknown'; code: string };

/** Human label for an inventory unit: item number, else #nr. */
export function unitLabel(unit: { item_number?: string | null; nr?: number | null }): string {
  return unit.item_number || (unit.nr != null ? `#${unit.nr}` : '?');
}

/** Look a scanned code up among the active merchant's items and products. */
export async function resolveBarcode(
  rawCode: string,
  merchantId: string | null
): Promise<{ data: ScanResolution | null; error: string | null }> {
  const code = normalizeBarcode(rawCode);
  if (!code) return { data: { kind: 'unknown', code: rawCode }, error: null };
  if (!merchantId) return { data: { kind: 'unknown', code }, error: null };

  try {
    const { data: items, error: itemError } = await supabase
      .from('inventory_items')
      .select(`
        id, nr, item_number, size, active, product_type_id, alt_product_type_id, additional_product_type_ids,
        product_types!inventory_items_product_type_id_fkey!inner(product_name, location_id, locations!inner(merchant_id))
      `)
      .eq('barcode', code)
      .eq('product_types.locations.merchant_id', merchantId)
      .limit(1);

    if (itemError) throw itemError;

    const item = (items || [])[0] as any;
    if (item) {
      const typeIds = [
        item.product_type_id,
        ...(item.alt_product_type_id ? [item.alt_product_type_id] : []),
        ...(item.additional_product_type_ids || []),
      ].filter((v: string, i: number, arr: string[]) => arr.indexOf(v) === i);

      return {
        data: {
          kind: 'unit',
          code,
          unit: {
            id: item.id,
            nr: item.nr,
            item_number: item.item_number,
            size: item.size,
            active: item.active !== false,
            product_type_id: item.product_type_id,
            product_type_ids: typeIds,
            product_name: item.product_types?.product_name || '',
            location_id: item.product_types?.location_id,
          },
        },
        error: null,
      };
    }

    const { data: products, error: productError } = await supabase
      .from('product_types')
      .select('id, product_name, inventory_type, location_id, locations!inner(merchant_id)')
      .eq('barcode', code)
      .eq('locations.merchant_id', merchantId)
      .limit(1);

    if (productError) throw productError;

    const product = (products || [])[0] as any;
    if (product) {
      return {
        data: {
          kind: 'product',
          code,
          product: {
            id: product.id,
            product_name: product.product_name,
            inventory_type: (product.inventory_type || 'individual') as ScannedProduct['inventory_type'],
            location_id: product.location_id,
          },
        },
        error: null,
      };
    }

    return { data: { kind: 'unknown', code }, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

export interface ScanBookingLine {
  id: string;
  product_type_id: string;
  inventory_item_id: string | null;
  size: string | null;
  item_name: string;
  quantity: number | null;
}

export interface ScanBooking {
  id: string;
  order_id: string;
  customer_firstname: string;
  customer_lastname: string;
  start_date: string;
  end_date: string;
  booking_state: string;
  location_id: string;
  /** The booking's lines that match the scan. */
  matchingLines: ScanBookingLine[];
}

/**
 * Bookings in the given states that contain a matching line, grouped by
 * booking. Scoped to the active merchant and the user's locations.
 */
async function findBookingsWithLines(params: {
  accessControl: AccessControl;
  states: string[];
  inventoryItemId?: string;
  productTypeIds?: string[];
  size?: string | null;
  matchSize?: boolean;
  startBefore?: string;
  startAfter?: string;
}): Promise<{ data: ScanBooking[]; error: string | null }> {
  try {
    let query = supabase
      .from('booking_items')
      .select(`
        id, product_type_id, inventory_item_id, size, item_name, quantity, is_bundle_parent,
        bookings!inner(id, order_id, customer_firstname, customer_lastname, start_date, end_date, booking_state, location_id, merchant_id)
      `)
      .in('bookings.booking_state', params.states)
      .eq('is_bundle_parent', false);

    if (params.accessControl.merchantId) {
      query = query.eq('bookings.merchant_id', params.accessControl.merchantId);
    }
    if (params.accessControl.locationIds && params.accessControl.locationIds.length > 0) {
      query = query.in('bookings.location_id', params.accessControl.locationIds);
    }
    if (params.inventoryItemId) {
      query = query.eq('inventory_item_id', params.inventoryItemId);
    }
    if (params.productTypeIds) {
      query = query.in('product_type_id', params.productTypeIds);
    }
    if (params.matchSize) {
      query = params.size ? query.eq('size', params.size) : query.is('size', null);
    }
    if (params.startBefore) {
      query = query.lte('bookings.start_date', params.startBefore);
    }
    if (params.startAfter) {
      query = query.gte('bookings.start_date', params.startAfter);
    }

    const { data, error } = await query.limit(500);
    if (error) throw error;

    const byBooking = new Map<string, ScanBooking>();
    for (const row of (data || []) as any[]) {
      const b = row.bookings;
      if (!b) continue;
      let entry = byBooking.get(b.id);
      if (!entry) {
        entry = {
          id: b.id,
          order_id: b.order_id,
          customer_firstname: b.customer_firstname,
          customer_lastname: b.customer_lastname,
          start_date: b.start_date,
          end_date: b.end_date,
          booking_state: b.booking_state,
          location_id: b.location_id,
          matchingLines: [],
        };
        byBooking.set(b.id, entry);
      }
      entry.matchingLines.push({
        id: row.id,
        product_type_id: row.product_type_id,
        inventory_item_id: row.inventory_item_id,
        size: row.size,
        item_name: row.item_name,
        quantity: row.quantity,
      });
    }

    const bookings = [...byBooking.values()].sort(
      (a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
    );
    return { data: bookings, error: null };
  } catch (error) {
    return { data: [], error: handleSupabaseError(error) };
  }
}

/**
 * How far back a not-yet-picked-up ('open') booking still counts as a pickup
 * candidate. Merchants who don't use Start / End order can have many stale
 * 'open' bookings; without a bound they would crowd out today's.
 */
const OVERDUE_PICKUP_DAYS = 7;

function overdueFloor(): string {
  return new Date(Date.now() - OVERDUE_PICKUP_DAYS * 24 * 3_600_000).toISOString();
}

/** Open or active bookings that have this unit allocated. */
export function findBookingsHoldingUnit(accessControl: AccessControl, unitId: string) {
  return findBookingsWithLines({ accessControl, states: ['open', 'active'], inventoryItemId: unitId });
}

/**
 * Not-yet-picked-up bookings starting by `endOfToday` (so today's bookings
 * plus ones overdue by up to OVERDUE_PICKUP_DAYS) with a line identical to the
 * unit: same product type (any type the unit can serve) and same size.
 */
export function findPickupCandidatesForUnit(accessControl: AccessControl, unit: ScannedUnit, endOfToday: string) {
  return findBookingsWithLines({
    accessControl,
    states: ['open'],
    productTypeIds: unit.product_type_ids,
    size: unit.size,
    matchSize: true,
    startBefore: endOfToday,
    startAfter: overdueFloor(),
  });
}

/** Bookings containing a pool / unlimited product: open ones up to `endOfToday`, and active ones. */
export async function findBookingsForProduct(accessControl: AccessControl, productTypeId: string, endOfToday: string) {
  const [open, active] = await Promise.all([
    findBookingsWithLines({ accessControl, states: ['open'], productTypeIds: [productTypeId], startBefore: endOfToday, startAfter: overdueFloor() }),
    findBookingsWithLines({ accessControl, states: ['active'], productTypeIds: [productTypeId] }),
  ]);
  return {
    data: [...open.data, ...active.data],
    error: open.error || active.error,
  };
}

export type UnitUnavailableReason =
  | { reason: 'inactive' }
  | { reason: 'blocked'; until: string; note: string | null }
  | { reason: 'booked'; orderId: string; from: string; state: string }
  | { reason: 'not_offered' };

/**
 * Why a unit can't be used for [start, end). Used to explain a refusal when
 * the unit is missing from check_availability_enhanced's result. Returns
 * not_offered when none of the specific reasons apply (e.g. the unit is not
 * offered at this location).
 */
export async function explainUnitUnavailable(
  unit: ScannedUnit,
  start: string,
  end: string,
  excludeBookingId?: string
): Promise<UnitUnavailableReason> {
  if (!unit.active) return { reason: 'inactive' };

  const { data: blocks } = await supabase
    .from('inventory_blocking_times')
    .select('blocked_from, blocked_until, blocking_reason, notes')
    .eq('inventory_item_id', unit.id)
    .lt('blocked_from', end)
    .gt('blocked_until', start)
    .order('blocked_until', { ascending: false })
    .limit(1);
  const block = (blocks || [])[0] as any;
  if (block) return { reason: 'blocked', until: block.blocked_until, note: block.notes || block.blocking_reason || null };

  let query = supabase
    .from('booking_items')
    .select('id, bookings!inner(id, order_id, start_date, end_date, booking_state)')
    .eq('inventory_item_id', unit.id)
    .in('bookings.booking_state', ['open', 'active'])
    .lt('bookings.start_date', end)
    .gt('bookings.end_date', start)
    .limit(5);
  if (excludeBookingId) query = query.neq('bookings.id', excludeBookingId);
  const { data: rows } = await query;
  const conflict = ((rows || []) as any[]).map((r) => r.bookings).find(Boolean);
  if (conflict) return { reason: 'booked', orderId: conflict.order_id, from: conflict.start_date, state: conflict.booking_state };

  return { reason: 'not_offered' };
}
